import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { ActiveFilter, CrawlfishJob, CrawlfishJobWithPages, DomainCount, JobInfo, Page, Pagination } from '../../crawler.interface';
import { CrawlfishJobWithPagesDefaultState } from '../../crawler.models';
import { _getJobById, _getPagesByJobId, _updateJob, } from '../../services/crawler.services';
import JobDetailMetadata from './jobDetailMetadata/jobDetailMetadata';
import './jobDetail.scss';
import { TableHeaderDataProps } from '../../../components/table/table.interface';
import Table from '../../../components/table/table';
import JobDetailRow from './jobDetailRow/jobDetailRow';
import { LeftArrow, LoaderIcon } from '../../../components/icons/icons';
import FilterSection from '../../../components/filterSection/filterSection';
import { JobDetailDataState, SortInfo } from './jobDetail.interface';
import { JobDetailDefaultFilters, JobDetailDefaultState } from './jobDetail.models';
import { __assignFilterStringDetailView, __serializeQueryParams, __translateFiltersToParamsDetailView, __translateParamsToFilters, __validateFilterObjects } from '../../../utils/filters';
import { useRequiredParams } from '../../../utils/hooks';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';
import UserContext, { UserContextInterface } from '../../../contexts/user-context';
import { _logOut } from '../../services/base.services';
import { _selectPages } from '../../services/report.services';

function JobDetail() {
    const { id } = useRequiredParams();

    const [job, setJob] = React.useState<CrawlfishJobWithPages>(() => CrawlfishJobWithPagesDefaultState);
    const [pagesData, setPagesData] = React.useState<JobDetailDataState>(JobDetailDefaultState);
    const { user } = React.useContext<UserContextInterface>(UserContext);

    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    async function init() {
        try {
            const fetchedJob = await _getJobById(id);
            if (fetchedJob) {
                setJob(fetchedJob);
                checkUrlParams(fetchedJob);
            }
        } catch (err) {
            console.error('Unable to fetch job data.');
        }
    }

    async function setPageCount() {
        try {
            const job = await _getJobById(id);
            if (job) {
                setJob({ ...job, info: { ...job.info, selected: job.info.selected } });
            }
        } catch (err) {
            console.error('Unable to fetch job data.');
        }
    }

    React.useEffect(() => {
        init();
    }, [id]);

    function checkUrlParams(activeJob: CrawlfishJob) {
        const serializedParams = __serializeQueryParams(searchParams);
        const paramsAsFilters = __translateParamsToFilters(serializedParams);

        if (serializedParams.length === 0 || _.isEqual(paramsAsFilters, JobDetailDefaultFilters)) {
            setPagesByFilters({ filters: JobDetailDefaultFilters, activeJob });
        } else if (paramsAsFilters) {
            try {
                __validateFilterObjects(paramsAsFilters);
                setPageByUrlParams(paramsAsFilters, activeJob);
            } catch (err) {
                setPagesData({
                    ...pagesData,
                    isFilterRunning: false,
                    emptyResultsMessage:
                        <span className="message filter-issue">There was a problem with the filter. Try clearing the filters via &quot;Clear All&quot; button and trying again</span>
                });
                console.error(err);
            }
        }
    }

    function setPageByUrlParams(filtersFromURL: ActiveFilter[], activeJob: CrawlfishJob): void {
        const offsetFilterIndex = filtersFromURL.findIndex((filter: ActiveFilter) => filter.name === 'page');
        if (offsetFilterIndex > -1) {
            const offset = filtersFromURL[offsetFilterIndex];
            const paginationFromURL = { offset: (Number(offset.value)) };

            // remove the values "page" and "limit" because they aren't filters
            filtersFromURL.splice(offsetFilterIndex, 1);
            const limitFilterIndex = filtersFromURL.findIndex((filter: ActiveFilter) => filter.name === 'limit');
            if (limitFilterIndex > -1) {
                filtersFromURL.splice(limitFilterIndex, 1);
            }

            setPagesByFilters({ pagination: paginationFromURL, filters: filtersFromURL, activeJob });
        } else {
            setPagesByFilters({ filters: filtersFromURL, activeJob });
        }
    }

    interface SetPagesByFiltersProps {
        pagination?: Pagination;
        filters: ActiveFilter[] | [];
        sortVal?: SortInfo;
        activeJob?: CrawlfishJob;
    }

    async function setPagesByFilters({
        pagination,
        filters = [],
        sortVal = { sortIndex: 0, activeColumn: '' },
        activeJob,
    }: SetPagesByFiltersProps): Promise<void> {

        setPagesData({ ...pagesData, isFilterRunning: true });
        const filterString = __assignFilterStringDetailView(filters, pagination);
        setUrlParams(filters, pagination);

        try {
            const pagesResponse = await _getPagesByJobId(id, filterString); // Check for ID
            if (pagesResponse) {
                const pages = pagesResponse.Results || [];
                if (pages.length == 0) {
                    setPagesData({
                        ...pagesData,
                        filteredPages: pages,
                        activeFilters: filters,
                        ResultCount: pagesResponse.ResultCount,
                        emptyResultsMessage: <span>No Pages found matching the filter option(s)</span>,
                        isFilterRunning: false,
                        sortInfo: {
                            sortIndex: sortVal.sortIndex !== 0 ? sortVal.sortIndex : pagesData.sortInfo.sortIndex,
                            activeColumn: sortVal.activeColumn.length > 0 ? sortVal.activeColumn : pagesData.sortInfo.activeColumn
                        },
                        isLoaded: true,
                    });
                } else {
                    const phraseOptions = activeJob?.keywords?.body || job.keywords?.body || [];
                    const domainOptions = generateDomainOptions(activeJob?.info || job.info);
                    if (sortVal.activeColumn === '') {
                        pages.sort((pageA: Page, pageB: Page) => {
                            return new Date(pageB.created_at).valueOf() - new Date(pageA.created_at).valueOf();
                        });
                    }
                    setPagesData({
                        ...pagesData,
                        filteredPages: pages,
                        activeFilters: filters,
                        domainOptions: domainOptions,
                        phraseOptions: phraseOptions,
                        isFilterRunning: false,
                        ResultCount: pagesResponse.ResultCount,
                        activePage: pagination?.offset || undefined,
                        sortInfo: {
                            sortIndex: sortVal.sortIndex !== 0 ? sortVal.sortIndex : pagesData.sortInfo.sortIndex,
                            activeColumn: sortVal.activeColumn.length > 0 ? sortVal.activeColumn : pagesData.sortInfo.activeColumn
                        },
                        isLoaded: true,
                    });
                }
            }
        } catch {
            console.log('Failed to get pages.');
        }
    }

    function setUrlParams(filters: ActiveFilter[], pagination?: Pagination): void {
        const filterParams = __translateFiltersToParamsDetailView(filters, pagination);
        setSearchParams(filterParams);
    }

    const handleReviewToggle = React.useCallback(async () => {
        const newJobObj = job;
        newJobObj.reviewed = !newJobObj.reviewed;
        await _updateJob(newJobObj);
        setJob(newJobObj);
    }, [job]);

    const sortByColumnHeader = async (header: string) => {
        const sortIndex = pagesData.sortInfo.sortIndex *= -1;
        const newSortFilter: ActiveFilter = {
            name: 'sort',
            value: sortIndex == -1 ? `-${header}` : header
        };

        const currentFilters = [...pagesData.activeFilters];
        currentFilters.forEach((currentFilter: ActiveFilter, index: number) => {
            // if sort exists, remove it
            if (currentFilter.name == 'sort') {
                currentFilters.splice(index, 1);
            }
        });
        setPagesByFilters({
            filters: [...currentFilters, newSortFilter],
            sortVal: { sortIndex, activeColumn: header }
        });
    };


    function handleRemoveActiveFilter(filter: ActiveFilter): void {
        const activeFilters = [...pagesData.activeFilters];
        const indexToRemove = activeFilters.findIndex((activeFilter: ActiveFilter) => (activeFilter.name === filter.name && activeFilter.value == filter.value));
        activeFilters.splice(indexToRemove, 1);
        setPagesByFilters({ filters: activeFilters });
    }

    async function handlePageFilterSubmit(filter: ActiveFilter): Promise<void> {
        const currentFilters = [...pagesData.activeFilters];
        currentFilters.forEach((currentFilter: ActiveFilter) => {
            if (currentFilter.name == filter.name) {
                const indexToRemove = currentFilters.findIndex((activeFilter: ActiveFilter) => activeFilter.name === filter.name);
                currentFilters.splice(indexToRemove, 1);
            }
        });
        if (filter.value !== '') {
            setPagesByFilters({ filters: [...currentFilters, filter] });
        } else {
            setPagesByFilters({ filters: [...currentFilters] });
        }
    }

    function clearActiveFilters(): void {
        setPagesByFilters({ filters: [] });
    }

    async function handlePageRowCheck(e: React.ChangeEvent<HTMLInputElement>, page: Page): Promise<void> {
        const checked = e.target.checked;
        try {
            await _selectPages(job.id, [page.id], checked, 'single');
            checkSinglePageRow(page, checked);
        } catch (err) {
            setPagesData({
                ...pagesData, isFilterRunning: false, emptyResultsMessage:
                    <span className="message">Page check failed</span>
            });
            console.error(err);
        }
    }

    function checkSinglePageRow(page: Page, checked: boolean): void {
        const filteredPages = [...pagesData.filteredPages];
        const index = filteredPages.indexOf(page);
        filteredPages[index].selected = checked;

        setPagesData({ ...pagesData, filteredPages: filteredPages });
        const currentSelectedPages = job.info.selected || 0;

        if (checked) {
            setJob({ ...job, info: { ...job.info, selected: currentSelectedPages + 1 } });
        } else {
            setJob({ ...job, info: { ...job.info, selected: currentSelectedPages - 1 } });
        }
    }

    // checks all pages, visible or not (via pagination), based on the applied filters
    async function handleCheckAll(checkAll: boolean): Promise<void> {
        const filteredPages = [...pagesData.filteredPages];
        const activeFilters = [...pagesData.activeFilters];
        // Remove sorting for checking 
        const index = activeFilters.findIndex((filter: ActiveFilter) => filter.name === 'sort');
        if (index > -1) {
            activeFilters.splice(index, 1);
        }

        const activeFilterString = __assignFilterStringDetailView(activeFilters);
        try {
            await _selectPages(job.id, [], checkAll, activeFilterString);
            const updatedPages = filteredPages.map((page: Page) => {
                page.selected = checkAll;
                return page;
            });
            setPageCount();
            setPagesData({ ...pagesData, filteredPages: updatedPages });
        } catch (err) {
            setPagesData({
                ...pagesData, isFilterRunning: false, emptyResultsMessage:
                    <span className="message">Check All failed, clear the filter and try again</span>
            });
            console.error(err);
        }

    }

    const jobDetailsHeaderData: TableHeaderDataProps[] = [
        {
            id: 1,
            title: 'URL',
            name: 'uri',
            className: 'urls',
            width: 35,
            func: sortByColumnHeader,
        },
        {
            id: 2,
            title: 'Traffic Source',
            name: 'source',
            className: 'traffic-source',
            width: 25,
            func: sortByColumnHeader
        },
        {
            id: 3,
            title: 'Page Type',
            name: 'page-type',
            className: 'page-type',
            width: 20,
            tooltip: 'Displays matched Page Types based on confidence level. High = Green, Medium = Yellow, Low = Red',
            func: sortByColumnHeader,
            sortDisabled: true
        },
        {
            id: 4,
            title: 'Phrases',
            name: 'keywords',
            className: 'phrases',
            width: 20,
            func: sortByColumnHeader,
            sortDisabled: true
        }
    ];

    const rows: JSX.Element[] = [];
    if (pagesData.filteredPages) {
        pagesData.filteredPages.forEach((page: Page) => {
            const newRow = <JobDetailRow key={page.id} page={page} handlePageRowCheck={handlePageRowCheck} />;
            rows.push(newRow);
        });
    }

    function generateDomainOptions(jobInfo: JobInfo): string[] {
        const domainOptions: string[] = [];
        if (jobInfo.domain_counts) {
            jobInfo.domain_counts.forEach((domainCountObj: DomainCount) => {
                if (!domainOptions.includes(domainCountObj.domain)) {
                    domainOptions.push(domainCountObj.domain);
                }
            });
            return domainOptions;
        }
        return [];
    }

    // Server Side Logic
    function setJobDetailPage(pageOffset: number): void {
        const paginationData = { offset: pageOffset };
        const activeFilters = [...pagesData.activeFilters];
        setUrlParams(activeFilters, paginationData);
        setPagesByFilters({ pagination: paginationData, filters: activeFilters });
    }

    return (
        <div className="job-detail-page" data-testid="job-detail">
            <div className="job-detail-header">
                <div className="left-side-header">
                    <h1>Job Details</h1>
                    <span className="back-arrow">
                        <a onClick={() => navigate('/jobs')}>
                            <LeftArrow />
                            Return to Jobs
                        </a>
                    </span>
                </div>
                <div className="right-side-header">
                    {user !== undefined &&
                        <>
                            <span>{user.email}</span>
                            <a className="logout-button" onClick={() => _logOut()}> Log out</a>
                        </>
                    }
                </div>
            </div>
            <JobDetailMetadata job={job} updateJob={setJob} toggleReview={handleReviewToggle} />

            <FilterSection
                activeFilters={pagesData.activeFilters}
                filterOptions={['URL', 'Traffic Source', 'Domain', 'Page Type', 'Phrase', 'Selected']}
                pageType="job-detail"
                domainOptions={pagesData.domainOptions}
                phraseOptions={pagesData.phraseOptions}
                handleFilterSubmit={handlePageFilterSubmit}
                handleFilterRemove={handleRemoveActiveFilter}
                clearActiveFilters={clearActiveFilters}
                jobId={job.id}
                jobName={job.name}
            />
            <div className="job-details-table">
                {rows ?
                    <Table
                        headerData={jobDetailsHeaderData}
                        bodyData={rows}
                        hasCheckAllOption={true}
                        handleCheckAll={handleCheckAll}
                        totalChecked={job.info?.selected}
                        activeColumn={pagesData.sortInfo.activeColumn}
                        sortIndex={pagesData.sortInfo.sortIndex}
                        pageDataLimit={200}
                        totalResults={pagesData.ResultCount.Total}
                        message={pagesData.emptyResultsMessage}
                        setPageServerSide={setJobDetailPage}
                        activePage={pagesData.activePage}
                        isFiltering={pagesData.isFilterRunning}
                    />
                    :
                    <LoaderIcon />
                }
            </div>
        </div>
    );
}

export default JobDetail;